import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import { useMemo } from 'react';

/**
 * Utility to check props of optionally controlled components (like html input),
 * that should not traverse between those two states.
 *
 * To get idea what optionally controlled components are, see
 * https://medium.com/quick-code/writing-ui-components-with-optionally-controllable-state-86e396a6f1ec
 *
 * This hook will raise error when given value changes from or to undefined, depending on its initial state.
 *
 * Example:
 *
 * ```
 * const CounterButton: React.FC<{
 *   count?: number;
 *   onIncrement?: () => void;
 * }> = props => {
 *   useOptionallyControlledPropGuard(count);
 *   // some code that will behave weird if count changes from or to undefined
 *   const [stateCount, stateIncrement] = useReducer(0, c => c + 1);
 *   const count = props.count ?? stateCount;
 *   // ...
 * }
 * ```
 */
export const useOptionallyControlledPropGuard = (value: unknown): void => {
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional, memo initial value
  const initialControlled = useMemo(() => value !== undefined, []);
  const currentControlled = value !== undefined;
  if (initialControlled !== currentControlled) {
    stumble(
      'Trying to change a component from controlled to uncontrolled behavior.' +
        ' This is not supported and will likely cause bugs.',
    );
  }
};
